import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { reportCardAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiSave, FiCheckCircle } from 'react-icons/fi';

const MarksEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const examId = searchParams.get('exam_id');

  const [exam, setExam] = useState(null);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (examId) {
      fetchData();
    }
  }, [examId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [examsRes, marksRes, subjectsRes] = await Promise.all([
        reportCardAPI.getOfflineExams({ id: examId }),
        reportCardAPI.getMarks(examId),
        masterAPI.getSubjects()
      ]);

      if (examsRes.data.success && examsRes.data.data.length > 0) {
        const examData = examsRes.data.data[0];
        setExam(examData);
        
        // Fetch students for this class
        const studentsRes = await masterAPI.getStudents({ class_id: examData.class_id });
        if (studentsRes.data.success) {
          setStudents(studentsRes.data.data);
        }
      }

      if (subjectsRes.data.success) {
        setSubjects(subjectsRes.data.data);
      }

      if (marksRes.data.success) {
        // Convert existing marks to state format
        const existingMarks = {};
        marksRes.data.data.forEach(mark => {
          const key = `${mark.student_id}_${mark.subject_id}`;
          existingMarks[key] = {
            marks_obtained: mark.marks_obtained,
            remarks: mark.remarks || ''
          };
        });
        setMarks(existingMarks);
      }
    } catch (error) {
      toast.error('Error loading data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarksChange = (studentId, subjectId, field, value) => {
    const key = `${studentId}_${subjectId}`;
    setMarks(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Convert marks object to array format
      const marksArray = [];
      Object.keys(marks).forEach(key => {
        const [studentId, subjectId] = key.split('_');
        if (marks[key].marks_obtained !== undefined && marks[key].marks_obtained !== '') {
          marksArray.push({
            student_id: parseInt(studentId),
            subject_id: parseInt(subjectId),
            marks_obtained: parseFloat(marks[key].marks_obtained),
            total_marks: parseInt(exam.total_marks),
            remarks: marks[key].remarks || ''
          });
        }
      });

      const response = await reportCardAPI.enterMarks({
        offline_exam_id: parseInt(examId),
        marks: marksArray
      });

      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/report-cards/exams');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save marks');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="p-4 md:p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Exam not found</p>
          <button
            onClick={() => navigate('/report-cards/exams')}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/report-cards/exams')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft /> Back to Exams
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          Marks Entry - {exam.exam_name}
        </h1>
        <p className="text-sm md:text-base text-gray-600 mt-1">
          Class: {exam.class_name} | Total Marks: {exam.total_marks}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50">
                    Student Name
                  </th>
                  {subjects.map(subject => (
                    <th key={subject.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {subject.subject}
                      <div className="text-xs font-normal text-gray-400 mt-1">
                        (Max: {exam.total_marks})
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {students.map(student => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 sticky left-0 bg-white">
                      {student.name}
                      <div className="text-xs text-gray-500">{student.email}</div>
                    </td>
                    {subjects.map(subject => {
                      const key = `${student.id}_${subject.id}`;
                      const currentMarks = marks[key] || {};
                      
                      return (
                        <td key={subject.id} className="px-4 py-3">
                          <div className="flex flex-col gap-2">
                            <input
                              type="number"
                              min="0"
                              max={exam.total_marks}
                              step="0.01"
                              value={currentMarks.marks_obtained || ''}
                              onChange={(e) => handleMarksChange(student.id, subject.id, 'marks_obtained', e.target.value)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-primary-600"
                              placeholder="0"
                            />
                            <input
                              type="text"
                              value={currentMarks.remarks || ''}
                              onChange={(e) => handleMarksChange(student.id, subject.id, 'remarks', e.target.value)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-primary-600"
                              placeholder="Remarks"
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300"
          >
            <FiSave /> {submitting ? 'Saving...' : 'Save All Marks'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/report-cards/exams')}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default MarksEntry;
